import { Injectable, inject, signal, ApplicationRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export type SupportedLocale = 'en' | 'fr' | 'es';

export const SUPPORTED_LOCALES: SupportedLocale[] = ['en', 'fr', 'es'];
export const DEFAULT_LOCALE: SupportedLocale = 'en';
export const LOCALE_STORAGE_KEY = 'tenant_portal_locale';

export interface LocaleOption {
  code: SupportedLocale;
  label: string;
}

export const LOCALE_OPTIONS: LocaleOption[] = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
];

/**
 * LocaleService acts as the central coordinator for all locale-related operations.
 * It wraps ngx-translate's TranslateService to centralize persistence, validation,
 * fallback logic, HTML lang attribute management, and LOCALE_ID updates.
 *
 * Requirements: 4.2, 4.5, 4.6, 4.7, 5.1, 5.2, 5.3, 5.4, 5.5, 6.1, 7.1, 7.3, 7.4, 8.1
 */
@Injectable({ providedIn: 'root' })
export class LocaleService {
  private readonly translate = inject(TranslateService);
  private readonly appRef = inject(ApplicationRef);
  private readonly activeLocale$ = new BehaviorSubject<SupportedLocale>(DEFAULT_LOCALE);

  /**
   * Signal holding the current locale ID value.
   * Used by the LOCALE_ID provider factory and can be read by components
   * to trigger re-rendering of date/number pipes when locale changes.
   */
  readonly localeSignal = signal<SupportedLocale>(DEFAULT_LOCALE);

  /** Observable of the currently active locale */
  readonly locale$: Observable<SupportedLocale> = this.activeLocale$.asObservable();

  /** Current active locale value */
  get currentLocale(): SupportedLocale {
    return this.activeLocale$.value;
  }

  /**
   * Initialize locale on app startup.
   * Reads localStorage, validates stored locale, sets active locale on TranslateService,
   * and updates the document lang attribute.
   */
  initialize(): void {
    const initialLocale = this.resolveInitialLocale();
    this.translate.setDefaultLang(DEFAULT_LOCALE);
    this.translate.use(initialLocale);
    this.activeLocale$.next(initialLocale);
    this.localeSignal.set(initialLocale);
    this.updateDocumentLang(initialLocale);
  }

  /**
   * Resolve the locale to use on first paint:
   *   1. A previously persisted, still-supported choice always wins.
   *   2. Otherwise infer from the browser's language(s); the first supported
   *      match is used and persisted so the choice is stable across reloads.
   *   3. Falls back to DEFAULT_LOCALE ('en') when nothing matches.
   */
  private resolveInitialLocale(): SupportedLocale {
    const stored = this.getStoredLocaleOrNull();
    if (stored) {
      return stored;
    }
    const inferred = this.detectBrowserLocale();
    // Persist the inferred choice so it doesn't change if the browser reports
    // languages in a different order later, and so switchLocale has a baseline.
    this.persistLocale(inferred);
    return inferred;
  }

  /**
   * Detect a supported locale from the browser's language preferences.
   * Checks `navigator.languages` (most-preferred first) then `navigator.language`,
   * matching only the primary subtag (e.g. "fr-CA" → "fr"). Returns DEFAULT_LOCALE
   * when no supported language is found.
   */
  detectBrowserLocale(): SupportedLocale {
    try {
      const candidates: string[] = [];
      const nav = typeof navigator !== 'undefined' ? navigator : undefined;
      if (nav?.languages?.length) {
        candidates.push(...nav.languages);
      }
      if (nav?.language) {
        candidates.push(nav.language);
      }
      for (const tag of candidates) {
        const primary = (tag || '').split('-')[0].toLowerCase();
        if (this.isSupportedLocale(primary)) {
          return primary;
        }
      }
    } catch {
      // navigator unavailable — fall through to default
    }
    return DEFAULT_LOCALE;
  }

  /**
   * Switch to a new locale, persist, and update document.
   * Returns an Observable that completes when the locale switch is done.
   * On error, retains the previous locale.
   */
  switchLocale(locale: SupportedLocale): Observable<void> {
    if (!this.isSupportedLocale(locale)) {
      return of(undefined);
    }

    const previousLocale = this.activeLocale$.value;

    return this.translate.use(locale).pipe(
      map(() => {
        this.activeLocale$.next(locale);
        this.localeSignal.set(locale);
        this.persistLocale(locale);
        this.updateDocumentLang(locale);
        // Trigger global change detection so Angular's built-in pipes
        // (DatePipe, DecimalPipe) re-evaluate with the new LOCALE_ID
        this.appRef.tick();
      }),
      catchError(() => {
        // Retain previous locale on error
        this.translate.use(previousLocale);
        this.activeLocale$.next(previousLocale);
        this.localeSignal.set(previousLocale);
        this.updateDocumentLang(previousLocale);
        throw new Error('Language switch failed. Please try again.');
      })
    );
  }

  /** Check if a locale code is supported */
  isSupportedLocale(value: string): value is SupportedLocale {
    return SUPPORTED_LOCALES.includes(value as SupportedLocale);
  }

  /**
   * Get stored locale from localStorage (validated).
   * Returns the stored locale if valid, otherwise returns DEFAULT_LOCALE
   * and replaces the invalid stored value.
   */
  private getStoredLocale(): SupportedLocale {
    const stored = this.getStoredLocaleOrNull();
    if (stored) {
      return stored;
    }
    // Invalid or missing stored value — replace with default
    this.persistLocale(DEFAULT_LOCALE);
    return DEFAULT_LOCALE;
  }

  /**
   * Return the persisted locale if present and supported, else null.
   * Unlike getStoredLocale(), this does NOT write a default — the caller
   * decides how to handle the absence (e.g. infer from the browser).
   */
  private getStoredLocaleOrNull(): SupportedLocale | null {
    try {
      const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
      if (stored && this.isSupportedLocale(stored)) {
        return stored;
      }
      return null;
    } catch {
      // localStorage unavailable (private browsing, quota exceeded, etc.)
      return null;
    }
  }

  /** Persist locale to localStorage */
  private persistLocale(locale: SupportedLocale): void {
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    } catch {
      // localStorage unavailable — silently continue without persistence
    }
  }

  /** Update document lang attribute */
  private updateDocumentLang(locale: SupportedLocale): void {
    document.documentElement.lang = locale;
  }
}
